import { unwrap, type components } from "@sagot/api-client";
import { ZOHO_INTEGRATION_KEY } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, Field, Icon, Input, Skeleton, toast, type IconName } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { wsKeys, type IntegrationOut } from "../../shared";
import { GOOGLE_CALENDAR_KEY } from "../use-google-calendar";
import { useIntegrations, useInvalidate } from "../use-settings";
import { useZohoReturn } from "../zoho-return";
import { GoogleCalendarCard } from "./google-calendar-card";
import { GOOGLE_SHEETS_KEY, GoogleSheetsCard } from "./google-sheets-card";
import { Gate, PanelError, useCanManage } from "./panel-kit";
import { ZohoCard } from "./zoho-card";
import { ZohoContactsSyncCard } from "./zoho-contacts-sync-card";

type IntegrationKey = components["schemas"]["IntegrationKey"];
/** Google Calendar, Google Sheets and Zoho CRM have their own OAuth cards; the others are configured with a key form. */
type FormIntegrationKey = Exclude<IntegrationKey, typeof GOOGLE_CALENDAR_KEY | typeof GOOGLE_SHEETS_KEY | typeof ZOHO_INTEGRATION_KEY>;

interface ConfigField {
  name: string;
  required: boolean;
}

/** Config fields per integration (backend `*Config` request models). */
export const INTEGRATION_FIELDS: Readonly<Record<FormIntegrationKey, readonly ConfigField[]>> = {
  hubspot: [
    { name: "portal_id", required: true },
    { name: "pipeline_id", required: false },
  ],
};

const ICONS: Readonly<Record<string, IconName>> = { hubspot: "target" };

/** Form values → API config (blank optional fields dropped). */
export function buildConfig(fields: readonly ConfigField[], values: Readonly<Record<string, string>>): Record<string, string> {
  const config: Record<string, string> = {};
  for (const field of fields) {
    const raw = (values[field.name] ?? "").trim();
    if (!raw) continue;
    config[field.name] = raw;
  }
  return config;
}

/** Saved config value (scalars only) or blank. */
function initialValue(saved: unknown): string {
  return typeof saved === "string" || typeof saved === "number" ? String(saved) : "";
}

function ConnectDialog({ integration, onClose }: { integration: IntegrationOut; onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const fields = INTEGRATION_FIELDS[integration.key as FormIntegrationKey];
  const initial = Object.fromEntries(fields.map((f) => [f.name, initialValue(integration.config[f.name])]));
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [busy, setBusy] = useState(false);
  const missing = fields.some((f) => f.required && !(values[f.name] ?? "").trim());
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await unwrap(api.PUT("/v1/integrations/{key}", { params: { path: { key: integration.key as IntegrationKey } }, body: { config: buildConfig(fields, values) } }));
      toast.success(t("settings.integrations.connected", { name: integration.name }));
      await invalidate(wsKeys.integrations(wsId));
      onClose();
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t("settings.integrations.connectTitle", { name: integration.name })} description={integration.description} closeLabel={t("common.close")}>
        <form className="space-y-4" onSubmit={(event) => void submit(event)}>
          {fields.map((field) => (
            <Field key={field.name} label={`${t(`settings.integrations.fields.${field.name}`)}${field.required ? "" : ` ${t("common.optional")}`}`}>
              <Input
                type="text"
                className="font-mono text-xs"
                value={values[field.name]}
                onChange={(event) => { setValues((v) => ({ ...v, [field.name]: event.target.value })); }}
              />
            </Field>
          ))}
          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={busy} disabled={missing}>
              {t("settings.integrations.connect")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IntegrationCard({ integration, onConnect }: { integration: IntegrationOut; onConnect: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const manage = useCanManage("integrations.manage");
  const connected = integration.status === "connected";
  const disconnect = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/integrations/{key}", { params: { path: { key: integration.key as IntegrationKey } } })),
    onSuccess: async () => {
      toast.info(t("settings.integrations.disconnected", { name: integration.name }));
      await invalidate(wsKeys.integrations(wsId));
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const reason = !integration.available ? t("settings.integrations.unavailableHint") : manage.reason;
  return (
    <article className="card card-body flex items-start gap-3" data-testid={`integration-${integration.key}`}>
      <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-sunken text-fg-muted">
        <Icon name={ICONS[integration.key] ?? "plug"} className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-bold">{integration.name}</h3>
          {connected ? (
            <Badge tone="success" dot>
              {t("settings.integrations.statusConnected")}
            </Badge>
          ) : integration.available ? (
            <Badge>{t("settings.integrations.statusDisconnected")}</Badge>
          ) : (
            <Badge>
              <Icon name="clock" className="size-3" />
              {t("settings.integrations.statusUnavailable")}
            </Badge>
          )}
        </div>
        <p className="mt-0.5 text-xs text-fg-muted">{integration.description}</p>
      </div>
      <Gate reason={reason}>
        {connected ? (
          <Button variant="secondary" size="sm" disabled={!manage.allowed} loading={disconnect.isPending} onClick={() => { disconnect.mutate(); }}>
            {t("settings.integrations.disconnect")}
          </Button>
        ) : (
          <Button size="sm" disabled={!manage.allowed || !integration.available} onClick={onConnect}>
            {t("settings.integrations.connect")}
          </Button>
        )}
      </Gate>
    </article>
  );
}

/** Integrations panel: CRM / Sheets / Calendar connectors from the API registry (also finishes a Zoho connect: `?zoho=`). */
export function IntegrationsPanel() {
  const { t } = useTranslation();
  const integrations = useIntegrations();
  useZohoReturn();
  const [connecting, setConnecting] = useState<IntegrationOut | null>(null);
  if (integrations.isError) return <PanelError error={integrations.error} onRetry={() => void integrations.refetch()} />;
  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-labelledby="integrations-title">
      <h2 id="integrations-title" className="sr-only">
        {t("settings.panels.integrations")}
      </h2>
      {integrations.isPending
        ? [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)
        : integrations.data.map((integration) => {
            if (integration.key === GOOGLE_CALENDAR_KEY) return <GoogleCalendarCard key={integration.key} integration={integration} />;
            if (integration.key === GOOGLE_SHEETS_KEY) return <GoogleSheetsCard key={integration.key} integration={integration} />;
            if (integration.key === ZOHO_INTEGRATION_KEY) return <ZohoCard key={integration.key} />;
            return <IntegrationCard key={integration.key} integration={integration} onConnect={() => { setConnecting(integration); }} />;
          })}
      <ZohoContactsSyncCard />
      {connecting ? <ConnectDialog integration={connecting} onClose={() => { setConnecting(null); }} /> : null}
    </section>
  );
}
